import { Shimmer } from "@/components/ai-elements/shimmer";
import AvatarCompany from "@/components/company/avatar-company";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useCompanyLinkInPanel } from "@/hooks/useCompanyLinkInPanel";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { internalLinkTarget } from "@/lib/internal-link-target";
import { CompanyIdentifierLookupState } from "@/lib/company-identifier-shortcut";
import {
  CompanyDTO,
  CompanyIdentifier,
  formatSirenFormatted,
  formatSiretFormatted,
} from "@shaarp/common";
import {
  AlertTriangleIcon,
  Loader2Icon,
  MessageSquareIcon,
  RotateCwIcon,
  SearchXIcon,
} from "lucide-react";
import {
  forwardRef,
  KeyboardEvent,
  RefObject,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";

/**
 * The value cmdk is held at while nothing is highlighted. cmdk highlights its
 * first item whenever the controlled value is empty, so "nothing" has to be a
 * value no row carries rather than `""`.
 */
const NO_ROW = "__none__";

/**
 * One row of the list: a single line, highlighted edge to edge rather than on
 * an inset pill — the prospecting search's own rhythm.
 */
const ROW = "gap-2 px-4 py-2.5 rounded-none cursor-pointer";

/** A line saying where the lookup stands, on that same grid. */
const STATE_ROW = `${ROW} cursor-default data-[disabled=true]:opacity-100`;

type ChatCompanyIdentifierDropdownProps = {
  readonly identifier: CompanyIdentifier;
  readonly lookup: CompanyIdentifierLookupState;
  /**
   * What a click outside of does not dismiss the dropdown: the composer it
   * belongs to, which the user goes on writing in.
   */
  readonly anchorRef: RefObject<HTMLElement | null>;
  /** A company page was opened: the composer can start over. */
  readonly onOpened: () => void;
  /** Send the message to the chat after all, the way it would have gone. */
  readonly onAskChat: () => void;
  readonly onRetry: () => void;
  /** Put the dropdown away and leave the message in the field, untouched. */
  readonly onClose: () => void;
};

export type ChatCompanyIdentifierDropdownRef = {
  /**
   * Arrow keys and Enter, handed over by the composer whose field keeps the
   * focus. `true` when the dropdown took the key, and the composer must not
   * act on it.
   */
  readonly handleKeyDown: (event: KeyboardEvent) => boolean;
};

/** The identifier as the user would write it, not as it is looked up. */
const displayedIdentifier = (identifier: CompanyIdentifier): string => {
  switch (identifier.type) {
    case "siret":
      // The very formatter that writes the number on the company's own card,
      // so the SIRET typed and the SIRET stored are never spelled two ways.
      return formatSiretFormatted(identifier.value) ?? identifier.value;
    case "siren":
      return formatSirenFormatted(identifier.value) ?? identifier.value;
    case "domain":
    case "name":
      return identifier.value;
    case "linkedin":
      try {
        return decodeURIComponent(identifier.value);
      } catch {
        return identifier.value;
      }
  }
};

/** Where a row leads. One expression, so the mouse and the keyboard agree. */
const companyPath = (company: CompanyDTO): string => `/company/${company.id}`;

/** The rows the dropdown can highlight, in the order the arrows walk them. */
type DropdownRow =
  | { readonly kind: "company"; readonly value: string; readonly company: CompanyDTO }
  | { readonly kind: "retry"; readonly value: string }
  | { readonly kind: "askChat"; readonly value: string };

/**
 * The companies an identifier typed alone in the chat designates, offered
 * above the composer in place of an answer — the prospecting search's
 * dropdown, brought to the chat.
 *
 * What the chat changes: it opens upwards, the composer sitting at the bottom
 * of the screen; the field it belongs to is a textarea rather than a
 * `CommandInput`, so the arrows and Enter are handed over by the composer
 * through `handleKeyDown` instead of reaching cmdk on their own; and it says
 * what became of the lookup — a registry that did not answer is not the same
 * thing as a company that does not exist, and both have a way on from here.
 *
 * Nothing was sent: the message is still in the field, and asking the chat is
 * one row away in every state.
 */
export const ChatCompanyIdentifierDropdown = forwardRef<
  ChatCompanyIdentifierDropdownRef,
  ChatCompanyIdentifierDropdownProps
>(function ChatCompanyIdentifierDropdown(
  { identifier, lookup, anchorRef, onOpened, onAskChat, onRetry, onClose },
  ref,
) {
  const { t } = useTranslation();
  const openInPanel = useCompanyLinkInPanel();
  const { isMobile } = useDeviceFormat();
  const navigate = useNavigate();

  const isLoading = lookup.status === "loading";
  const isUnavailable =
    lookup.status === "failed" ||
    (lookup.status === "settled" && lookup.result.status === "unavailable");
  const isNotFound =
    lookup.status === "settled" && lookup.result.status === "notFound";

  const companies =
    lookup.status === "settled" && lookup.result.status === "found"
      ? [lookup.result.company, ...lookup.result.otherCompanies]
      : [];

  const rows: DropdownRow[] = [
    ...companies.map(
      (company): DropdownRow => ({ kind: "company", value: company.id, company }),
    ),
    ...(isUnavailable ? [{ kind: "retry" as const, value: "retry" }] : []),
    { kind: "askChat", value: "askChat" },
  ];

  /**
   * What Enter lands on before the user has touched an arrow. An identifier
   * was typed on purpose and its first company is the answer to it, so Enter
   * opens that company. A name is only the composer's own reading of the
   * message: nothing is highlighted, and Enter sends the message as it always
   * has.
   */
  const defaultRow =
    identifier.type !== "name" && companies.length > 0
      ? companies[0].id
      : NO_ROW;

  const [activeRow, setActiveRow] = useState(defaultRow);

  // A lookup that settles, or another company under the same identifier,
  // re-arms Enter on the answer now on screen.
  useEffect(() => setActiveRow(defaultRow), [defaultRow]);

  // Enter on a row does what a tap on its link does: the panel on a phone, a
  // tab of its own on a desktop — the conversation is the user's place of
  // work — and in place in the app installed on iOS, where a new tab is
  // Safari, signed out (internalLinkTarget).
  const openCompany = (company: CompanyDTO): void => {
    if (isMobile) {
      openInPanel(null, {
        id: company.id,
        label: company.name,
      });
    } else if (internalLinkTarget() === "_blank") {
      window.open(companyPath(company), "_blank", "noopener,noreferrer");
    } else {
      navigate(companyPath(company));
    }
    onOpened();
  };

  const selectRow = (row: DropdownRow): void => {
    switch (row.kind) {
      case "company":
        return openCompany(row.company);
      case "retry":
        return onRetry();
      case "askChat":
        return onAskChat();
    }
  };

  useImperativeHandle(ref, () => ({
    handleKeyDown(event: KeyboardEvent): boolean {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const step = event.key === "ArrowDown" ? 1 : -1;
        const from = rows.findIndex((row) => row.value === activeRow);
        // Nothing highlighted yet: Down starts at the top of the list, Up at
        // its bottom.
        const next =
          from === -1
            ? step === 1
              ? 0
              : rows.length - 1
            : (from + step + rows.length) % rows.length;
        setActiveRow(rows[next]?.value ?? NO_ROW);
        return true;
      }

      if (event.key === "Enter" && !event.shiftKey) {
        const row = rows.find((candidate) => candidate.value === activeRow);
        if (!row) {
          return false;
        }
        event.preventDefault();
        selectRow(row);
        return true;
      }

      return false;
    },
  }));

  // Clicking away puts the dropdown down, as it does on the prospecting
  // search. The composer is not "away": the user goes on writing in it.
  useEffect(() => {
    const dismiss = (event: MouseEvent): void => {
      const anchor = anchorRef.current;
      if (anchor && !anchor.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [anchorRef, onClose]);

  return (
    <div
      // A live region, not an alert: it reports on what the user is typing,
      // it does not interrupt them.
      role="status"
      className="absolute right-0 bottom-full left-0 z-50 mb-2 text-left duration-150 animate-in fade-in slide-in-from-bottom-2"
    >
      {/* The prospecting search's own panel: white, deeply rounded, floating
          on a shadow rather than boxed in by a border. */}
      <Command
        shouldFilter={false}
        value={activeRow}
        onValueChange={setActiveRow}
        className="overflow-hidden bg-white rounded-2xl shadow-2xl dark:bg-gray-900"
      >
        <CommandList className="max-h-[280px]">
          <CommandGroup
            className="p-0 **:[[cmdk-group-heading]]:px-4 **:[[cmdk-group-heading]]:pt-3 **:[[cmdk-group-heading]]:pb-1.5"
            // What the composer recognised, in every state: the list below
            // says what became of it.
            heading={t(
              `chat.input.companyIdentifier.identifier.${identifier.type}`,
              { value: displayedIdentifier(identifier) },
            )}
          >
            {/* Told, not offered: what became of the lookup is a line of the
                list and never a row the arrows can land on. cmdk dims a
                disabled item by half, which the muted colours already say. */}
            {isLoading && (
              <CommandItem disabled value="loading" className={STATE_ROW}>
                <Loader2Icon className="animate-spin text-muted-foreground" />
                <Shimmer>{t("chat.input.companyIdentifier.loading")}</Shimmer>
              </CommandItem>
            )}
            {isNotFound && (
              <CommandItem disabled value="notFound" className={STATE_ROW}>
                <SearchXIcon className="text-muted-foreground" />
                <span className="text-muted-foreground">
                  {t("chat.input.companyIdentifier.notFound.title")}
                </span>
              </CommandItem>
            )}
            {/* An outage must never read as "no such company": the retry
                right below is the difference, and it is the whole message. */}
            {isUnavailable && (
              <CommandItem disabled value="unavailable" className={STATE_ROW}>
                <AlertTriangleIcon className="text-amber-600 dark:text-amber-500" />
                <span className="text-muted-foreground">
                  {t("chat.input.companyIdentifier.unavailable.title")}
                </span>
              </CommandItem>
            )}
            {/* Several companies on file can carry one identifier — the
                establishments of a SIREN, the franchisees of a website, the
                namesakes of a name, the duplicate records of a SIRET. The
                dropdown never passes its best record off as the only one: it
                lists them, best first, and the user says which — by the logo,
                as the prospecting search has them do. */}
            {companies.map((company) => (
              <CommandItem
                key={company.id}
                value={company.id}
                asChild
                className={ROW}
              >
                {/* Internal route, opened in a new tab on a desktop: the
                    conversation is the user's place of work, and losing it to
                    read a company page is not a trade they asked for. On a
                    phone it opens in place (internalLinkTarget). */}
                <Link
                  to={companyPath(company)}
                  target={internalLinkTarget()}
                  rel="noreferrer"
                  onClick={(event) => {
                    openInPanel(event, { id: company.id, label: company.name });
                    onOpened();
                  }}
                >
                  <AvatarCompany company={company} className="shrink-0 size-6" />
                  <span className="truncate">{company.name}</span>
                </Link>
              </CommandItem>
            ))}
          </CommandGroup>
          <CommandSeparator />
          {/* The way back to the chat, offered in every state — and never
              further than the bottom of the list. */}
          <CommandGroup className="p-0">
            {isUnavailable && (
              <CommandItem value="retry" onSelect={onRetry} className={ROW}>
                <RotateCwIcon className="text-muted-foreground" />
                {t("chat.input.companyIdentifier.retry")}
              </CommandItem>
            )}
            <CommandItem value="askChat" onSelect={onAskChat} className={ROW}>
              <MessageSquareIcon className="text-muted-foreground" />
              {t("chat.input.companyIdentifier.askChat")}
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </div>
  );
});
